<template>
    <div id="inquiry-page">
        <!-- HEADER -->
        <div class="action-header">
            <div class="d-flex-wrap">
                <div class="search-container">
                    <div class="search-input">
                        <v-text-field
                            v-model="search"
                            prepend-inner-icon="search"
                            :placeholder="this.$vuetify.lang.t('$vuetify.inquiry.searchByEmployeeName')"
                            solo
                            height="40px"
                            @keyup.enter="searchInquiries()">
                        </v-text-field>
                        <v-btn
                            class="ml-3"
                            height="40px"
                            color="primary"
                            outlined
                            @click="searchInquiries()">
                            {{ $vuetify.lang.t('$vuetify.inquiry.search') }}
                        </v-btn>
                    </div>

                    <div class="filter-input">
                        <!-- <div class="mr-3 date-range-input"> -->
                            <date-picker-range
                                class="mr-3"
                                v-model="dateRange" />
                        <!-- </div> -->
                        <div class="mr-3 select-filter">
                            <v-select
                                @change="searchInquiries()"
                                v-model="statusFilter"
                                :items="statusList"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                height="40"
                                background-color="#ffffff">
                            </v-select>
                        </div>
                        <div class="mr-3 select-filter">
                            <v-select
                                @change="searchInquiries()"
                                v-model="bureauFilter"
                                :items="bureauList"
                                item-text="text"
                                item-value="value"
                                outlined
                                dense
                                height="40"
                                background-color="#ffffff">
                            </v-select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- END HEADER -->

        <!-- DATATABLE -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @click:row="inquiryInfo"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.inquiryText`]="{ item }">
                    <span
                        class="inquiry-text">
                        {{ item.inquiryText }}
                    </span>
                </template>
                <template v-slot:[`item.statusText`]="{ item }">
                    <span
                        :class="isPending(item.status)">
                        {{ item.statusText }}
                    </span>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <div class="action" v-if="canUpdate">
                        <div :class="actionStyle(item.status)">
                            <i class="mr-3 far fa-edit" @click.stop="editInquiry(item.id)"></i>
                        </div>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <!-- END DATATABLE -->

        <!-- PAGINATION -->
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <!-- END PAGINATION -->
    </div>
</template>

<script>
import DatePickerRange from '@/components/DatePickerRange.vue'

export default {
    name: 'Inquiry',

    components: {
        DatePickerRange
    },

    data () {
        return {
            // Filter
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            loading: false,
            dateRange: [this.$dateUtil.getCurrentDate(), this.$dateUtil.getCurrentDate()],
            bureauFilter: null,
            statusFilter: null,
            bureauList: [],
            statusList: [],
            canUpdate: false
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.inquiry.employeeId'), value: 'employeeId', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.employeeName'), value: 'fullname', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.bureau'), value: 'bureau', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.position'), value: 'position', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.submittedDate'), value: 'submitted_date', width: '12%' },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.inquiryText'), value: 'inquiryText', width: '15%', sortable: false },
                { text: this.$vuetify.lang.t('$vuetify.inquiry.status'), value: 'statusText', width: '10%' },
                { text: '', value: 'action', width: '8%', align: 'center', sortable: false }
            ]
        }
    },

    methods: {
        async initInquiry () {
            await this.getBureaus()
            await this.getStatus()
            await this.getInquiries()
        },

        async searchInquiries () {
            this.options.page = 1
            await this.getInquiries()
        },

        async getInquiries () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `inquiries?page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.dateRange.length > 0) {
                url += `&dateRange=${JSON.stringify(this.dateRange)}`
            }

            if (this.statusFilter) {
                url += `&status=${this.statusFilter}`
            }

            if (this.bureauFilter) {
                url += `&bureau=${this.bureauFilter}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
            }
            this.loading = false
        },

        async getBureaus () {
            const res = await this.axios.get('bureau/options')
            if (res && res.data && res.data.status === 'ok') {
                this.bureauList = res.data.data
            }
        },

        async getStatus () {
            const res = await this.axios.get('inquiry/status/options')
            if (res && res.data && res.data.status === 'ok') {
                this.statusList = res.data.data
            }
        },

        actionStyle (status) {
            return status === 'PND' ? 'action-enable' : 'action-disable'
        },

        editInquiry (id) {
            this.$router.push({ name: 'InquiryForm', params: { id: id, mode: 'edit' } }, () => {})
        },

        isPending (status) {
            return status === 'PND' ? 'pending-style' : ''
        },

        inquiryInfo (item) {
            this.$router.push({ name: 'InquiryForm', params: { id: item.id, mode: 'view' } })
        }
    },

    async mounted () {
        this.canUpdate = await this.hasPermission(this.$constant.permission.ATTENDANCE.UPDATE)
    },

    watch: {
        options: {
            async handler () {
                await this.initInquiry()
            }
        },

        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.initInquiry()
            },
            deep: true
        },

        dateRange: {
            async handler () {
                if (this.dateRange.length === 0 || this.dateRange.length === 2) {
                    await this.searchInquiries()
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    .search-container {
        ::v-deep .v-input__control > .v-input__slot {
            min-height: auto !important;
            display: flex !important;
            align-items: center !important;
            box-shadow: none !important;
            border: 1px solid #a8a8a8 !important;
            width: 280px;
        }

        ::v-deep .search-input .v-input__control > .v-input__slot {
            width: 340px;
        }

        ::v-deep .date-range-input .v-input__control > .v-input__slot {
            width: 220px;
        }
    }

    ::v-deep .select-filter {
        .v-input__control > .v-input__slot {
            border: none !important;
            width: 200px;
        }

        .v-select__selection{
            line-height: 1.75;
        }
    }

    ::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
        margin: 0 !important;
    }

    ::v-deep .theme--light.v-data-table tbody tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
        background: #eeeeee;
        cursor: pointer;
    }

    .d-flex-wrap {
        display: flex;
        flex-wrap: wrap;
    }

    .action {
        display: flex;
        align-items: center;

        .action-enable i {
            cursor: pointer;
            color: #6c6c6c !important;
            font-weight: 400 !important;
            padding: 15px;
        }

        .action-disable i {
            pointer-events: none;
            cursor: not-allowed;
            color: #a8a8a8 !important;
            font-weight: 200 !important;
            padding: 15px;
        }
    }

    .inquiry-text {
        color: #aa1a11;
    }

    .pending-style {
        color: #ff544a;
    }

    .ml-day-inquiry {
        margin-left: 40px;
    }

    .highlight {
        padding: 0rem 1rem;
        background-color: #ff544a;
        border-radius: 25px;
        color: white;
    }
    .success {
        padding: 0rem 1rem;
        background-color: #5cb85c;
        border-radius: 25px;
        color: white;
    }

    .editable {
        cursor: pointer;
    }
</style>
